import { _ } from "../core/polyglot/polyglot.js";
import Model from "./Model.js";

/**
 * `EmailContent` keeps the different fields needed for email notification
 * and make sure they are fit to be sent.
 * Can be used as a visitor to update its content.
 *
 * @param {string} spec.subject - string to be used as the email title.
 * @param {string} spec.body - html string defining the email body text.
 * @param {User} spec.sender - User that will send the email.
 */
export default class EmailContent extends Model {
	constructor({ subject, body, sender } = {}) {
		super(...arguments);

		this._subject = subject;
		this._body = body;
		this._sender = sender;

		this._recipients = [];
		this._wasEdited = false;
	}

	visit(object) {
		return object.accept(this);
	}

	visitPeriodReport(periodReport) {
		const bodyText = _(
			"Hi,<br><br>There is a new report ready for you.<br>Click the button below to log in and access the report.<br><br>Kind regards,",
		);

		this.setBody(`${bodyText}<br>${this._sender.getName()}`);
		this.setSubject(periodReport.getTitle());

		return this;
	}

	visitPeriodReportDraft(periodReport) {
		const bodyText = _(
			"Hi,<br><br>There is a new draft ready for you to review.<br>Click the button below to log in and access the report.<br><br>Kind regards,",
		);

		this.setBody(`${bodyText}<br>${this._sender.getName()}`);
		this.setSubject(periodReport.getTitle());

		return this;
	}

	visitPeriodReportSummary(periodReportSummary) {
		return this.visitPeriodReport(periodReportSummary);
	}

	visitForecast(forecast) {
		const bodyText = _(
			"Hi,<br><br>There is a new forecast report ready for you.<br>Click the button below to log in and access the report.<br><br>Kind regards,",
		);

		this.setBody(`${bodyText}<br>${this._sender.getName()}`);

		this.setSubject(
			_("Forecast for {period}", {
				period: forecast.getLastReferencePeriod().asString(),
			}).toString(),
		);

		return this;
	}

	getSubject() {
		return this._subject;
	}

	setSubject(subject) {
		this._subject = subject;
	}

	getBody() {
		return this._body;
	}

	setBody(body) {
		this._body = body;
	}

	getSender() {
		return this._sender;
	}

	getRecipients() {
		return this._recipients;
	}

	setRecipients(users) {
		this._recipients = users;
	}

	wasEdited() {
		return this._wasEdited;
	}

	setEdited(bool) {
		this._wasEdited = bool;
	}

	validate({ collector }) {
		collector.addErrorIf(
			!this._recipients.length,
			_("No recipient was selected."),
		);

		collector.addErrorIf(
			this._isFieldEmpty(this._subject),
			_("Notification subject was empty."),
		);

		collector.addErrorIf(
			this._isFieldEmpty(this._body),
			_("Notification text was empty."),
		);
	}

	_serializedProperties() {
		return {
			sender: this._sender,
			recipients: this._recipients.map((user) => user.getId()),
			subject: this._subject,
			body: this._body,
		};
	}

	_isFieldEmpty(string) {
		return !string || this._stringify(string) === "";
	}

	_stringify(string) {
		const stringified = string
			.toString()
			.replace(/<p>/g, "")
			.replace(/<\/p>/g, "")
			.replace(/<br>/g, "");

		return stringified.trim();
	}
}
